import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import {
  fetchProject,
  fetchProjectFilterOptions,
  fetchProjects,
  fetchProjectSupportItems,
  type ProjectFilterOptions,
} from '../api/projects.api';
import { queryKeys } from '@/shared/lib/queryKeys';
import type {
  Id,
  ProjectFilters,
  ProjectSupportItem,
  ProjectWithContext,
} from '@/shared/types/domain';

/**
 * Listagem de projetos, com recorte opcional.
 *
 * A chave carrega os filtros (`queryKeys.projects.list`), então cada recorte
 * tem cache próprio e voltar para um filtro já visitado é instantâneo. É
 * também por isso que `invalidateAfterTransition` invalida o prefixo
 * `['projects']` inteiro: um projeto aprovado precisa sumir de "aguardando
 * decisão" em TODOS os recortes em cache, não só no que está na tela.
 */
export function useProjects(filters: ProjectFilters = {}): UseQueryResult<ProjectWithContext[]> {
  return useQuery({
    queryKey: queryKeys.projects.list(filters),
    queryFn: () => fetchProjects(filters),
    staleTime: 30_000,
  });
}

export function useProject(id: Id | undefined): UseQueryResult<ProjectWithContext | null> {
  return useQuery({
    queryKey: queryKeys.projects.detail(id ?? ''),
    queryFn: () => fetchProject(id as Id),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}

/** Opções dos seletores de filtro. Muda quando a secretaria cadastra algo. */
export function useProjectFilterOptions(): UseQueryResult<ProjectFilterOptions> {
  return useQuery({
    queryKey: [...queryKeys.projects.all, 'filter-options'],
    queryFn: fetchProjectFilterOptions,
    staleTime: 5 * 60_000,
  });
}

export function useProjectSupportItems(id: Id | undefined): UseQueryResult<ProjectSupportItem[]> {
  return useQuery({
    queryKey: [...queryKeys.projects.detail(id ?? ''), 'support-items'],
    queryFn: () => fetchProjectSupportItems(id as Id),
    enabled: Boolean(id),
    staleTime: 30_000,
  });
}
